import type { Locale, UserPlan, UserProfile } from "@/api/types";
import { HttpClient } from "@/shared/api/http-client";
import { isRecord } from "@/shared/api/envelope";

export interface SessionUser {
  id: string;
  email: string;
  display_name?: string | null;
  avatar_url?: string | null;
  email_verified?: boolean;
  locale?: string;
  timezone?: string;
  onboarding_progress?: number;
  plan?: UserPlan;
  email_overview_enabled?: boolean;
  created_at?: string;
}

export interface CreatedSession {
  user: SessionUser;
  session: {
    id: string;
    expires_at: string;
  };
}

export interface DeviceSession {
  id: string;
  client: "web" | "extension";
  user_agent?: string;
  ip_address?: string;
  created_at: string;
  last_seen_at?: string;
  expires_at: string;
  current: boolean;
}

export function mapSessionUserToProfile(user: SessionUser): UserProfile {
  const locale: Locale = user.locale === "id" ? "id" : "en";
  return {
    user_id: user.id,
    email: user.email,
    plan: user.plan === "pro" ? "pro" : "free",
    monetization_enabled: false,
    summaries_used_this_month: 0,
    limits: null,
    locale,
    timezone: user.timezone || "UTC",
    onboarding_progress: user.onboarding_progress ?? 0,
    display_name: user.display_name ?? undefined,
    avatar_url: user.avatar_url ?? undefined,
    email_overview_enabled: user.email_overview_enabled,
  };
}

function parseSessionUser(value: unknown): SessionUser {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.email !== "string") {
    throw new Error("Invalid session user");
  }
  return {
    id: value.id,
    email: value.email,
    display_name: typeof value.display_name === "string" ? value.display_name : null,
    avatar_url: typeof value.avatar_url === "string" ? value.avatar_url : null,
    email_verified: typeof value.email_verified === "boolean" ? value.email_verified : undefined,
    locale: typeof value.locale === "string" ? value.locale : undefined,
    timezone: typeof value.timezone === "string" ? value.timezone : undefined,
    onboarding_progress:
      typeof value.onboarding_progress === "number" ? value.onboarding_progress : undefined,
    plan: value.plan === "pro" || value.plan === "free" ? value.plan : undefined,
    email_overview_enabled:
      typeof value.email_overview_enabled === "boolean" ? value.email_overview_enabled : undefined,
    created_at: typeof value.created_at === "string" ? value.created_at : undefined,
  };
}

export function createSessionApi(http: HttpClient) {
  return {
    async createSession(firebaseIdToken: string): Promise<CreatedSession> {
      const data = await http.request<unknown>("POST", "/api/v1/auth/session", undefined, {
        bearer: firebaseIdToken,
        auth: false,
        unwrapData: true,
      });
      if (!isRecord(data) || !isRecord(data.user) || !isRecord(data.session)) {
        throw new Error("Invalid session response");
      }
      if (typeof data.session.id !== "string" || typeof data.session.expires_at !== "string") {
        throw new Error("Invalid session response");
      }
      return {
        user: parseSessionUser(data.user),
        session: { id: data.session.id, expires_at: data.session.expires_at },
      };
    },

    async deleteSession(pairingClientId?: string): Promise<void> {
      await http.request("DELETE", "/api/v1/auth/session", undefined, {
        auth: false,
        headers: pairingClientId ? { "X-Memoru-Pairing-Client": pairingClientId } : undefined,
        responseType: "empty",
      });
    },

    async getMe(): Promise<UserProfile> {
      const data = await http.request<unknown>("GET", "/api/v1/me", undefined, {
        unwrapData: true,
        // Session probe: a 401 means anonymous. Recovering here deadlocks
        // refreshSession → getMe → onUnauthorized → refreshSession.
        auth: false,
      });
      return mapSessionUserToProfile(parseSessionUser(data));
    },

    async updateLocale(locale: Locale): Promise<UserProfile> {
      const data = await http.request<unknown>("PATCH", "/api/v1/me", { locale }, { unwrapData: true });
      return mapSessionUserToProfile(parseSessionUser(data));
    },

    async updateDisplayName(displayName: string): Promise<UserProfile> {
      const data = await http.request<unknown>("PATCH", "/api/v1/me", { display_name: displayName }, { unwrapData: true });
      return mapSessionUserToProfile(parseSessionUser(data));
    },

    async updateEmailOverviewEnabled(enabled: boolean): Promise<UserProfile> {
      const data = await http.request<unknown>("PATCH", "/api/v1/me", { email_overview_enabled: enabled }, { unwrapData: true });
      return mapSessionUserToProfile(parseSessionUser(data));
    },

    async listSessions(): Promise<DeviceSession[]> {
      const data = await http.request<unknown>("GET", "/api/v1/me/sessions", undefined, {
        unwrapData: true,
      });
      if (!Array.isArray(data)) throw new Error("Invalid sessions response");
      return data.map((item) => {
        if (!isRecord(item) || typeof item.id !== "string") throw new Error("Invalid session");
        return {
          id: item.id,
          client: item.client === "extension" ? "extension" : "web",
          user_agent: typeof item.user_agent === "string" ? item.user_agent : undefined,
          ip_address: typeof item.ip_address === "string" ? item.ip_address : undefined,
          created_at: typeof item.created_at === "string" ? item.created_at : "",
          last_seen_at: typeof item.last_seen_at === "string" ? item.last_seen_at : undefined,
          expires_at: typeof item.expires_at === "string" ? item.expires_at : "",
          current: item.current === true,
        };
      });
    },

    async revokeSession(sessionId: string): Promise<void> {
      await http.request(
        "DELETE",
        `/api/v1/me/sessions/${encodeURIComponent(sessionId)}`,
        undefined,
        { responseType: "empty" },
      );
    },
  };
}
